import InputTypesEnum from '../../../Enums/InputTypesEnum';
import ValidityHelper from '../../../Helpers/ValidityHelper';
import OfficeOverheadService from '../../../Services/OfficeOverheadService';
import OfficeService from '../../../Services/OfficeService';
import ErrorMessage from '../../../Elements/ErrorMessage';

// Trivial placeholder: the wizard only instantiates windowDescription.itemType when no
// editedItem prop is passed. OfficeAdmin always passes AddNewOfficeOverheadWindowDescription.buildItem()
// (crudType=Create with an explicit editedItem), so this is never invoked, but it keeps the
// windowDescription contract satisfied.
class AddNewOfficeOverheadEditItem {}

export default class AddNewOfficeOverheadWindowDescription {
  static titleForCreate = 'Create Office Overhead';
  static itemType = AddNewOfficeOverheadEditItem;
  static finishButtonLabel = 'Save';
  static messageOnSuccess = 'Office overhead has been created.';

  static buildItem() {
    return {
      SelectedOfficeIds: [],
      Quarter: '',
      QuarterOptions: [{ label: '', value: '' }],
      List: [],
      RateRows: [],
    };
  }

  // Mirrors the old panel's clickOnSubmitButton: finds the List entry for the chosen Quarter and
  // posts that whole entry back. MUST return the promise (wizard contract).
  static serviceFunction = (edited) => {
    const entry = edited.List.find((q) => q.Quarter === edited.Quarter);
    return OfficeOverheadService.CreateOfficeOverhead(entry);
  };

  // Whole-array check used by the wizard's form-validity sweep (mirrors the old panel's
  // validityCheckerForTheEntireForm over the selected quarter's rows).
  static validityCheckerForAllRows(rows, rateField) {
    return rows.some((row) => ValidityHelper.forRequiredNumberFieldGreaterThanZero(row[rateField]) !== '')
      ? 'Every office needs an overhead rate greater than zero.'
      : '';
  }

  // Repoints RateRows at the chosen quarter's rows whenever the Quarter select changes.
  // Synchronous only - onFieldChange's return value is written straight into editedItem and must
  // never be a Promise (see GeneralEditWizzardPageFigma.updateRelatedData).
  static syncRateRows(item) {
    const entry = item.List.find((q) => q.Quarter === item.Quarter);
    item.RateRows = entry ? entry.OfficeOverheadList : [];
  }

  // Host onChangeEditedItem: runs on every field change (offices, quarter, and each rate edit).
  // Only re-fetches when the office selection itself changed, tracked via LastFetchedOfficeIds
  // stashed on the item (order-insensitive compare so re-ordering the same offices doesn't
  // trigger a refetch either).
  static async onChangeEditedItem(item) {
    const previous = item.LastFetchedOfficeIds ?? [];
    const current = item.SelectedOfficeIds ?? [];
    const sameSelection = JSON.stringify([...previous].sort()) === JSON.stringify([...current].sort());
    if (sameSelection) return;

    item.LastFetchedOfficeIds = [...current];

    try {
      const result = await OfficeOverheadService.GetOfficeOverhead(current);
      const list = OfficeOverheadService.ConvertAvailableQuarterList(result);

      const uniqueOfficeIds = [...new Set(list.flatMap((entry) => entry.OfficeOverheadList.map((row) => row.OfficeId)))];
      const names = await Promise.all(
        uniqueOfficeIds.map((officeId) => OfficeService.GetOfficeNameById(officeId).catch(() => ''))
      );
      const nameByOfficeId = new Map(uniqueOfficeIds.map((officeId, index) => [officeId, names[index]]));

      list.forEach((entry) => {
        entry.OfficeOverheadList.forEach((row) => {
          const name = nameByOfficeId.get(row.OfficeId) ?? '';
          row.PricingLabel = `${name} — Pricing Overhead Rate`;
          row.ActualLabel = `${name} — Actual Overhead Rate`;
        });
      });

      item.List = list;
      item.QuarterOptions = [{ label: '', value: '' }, ...OfficeOverheadService.GetQuarterListName(list)];
      item.Quarter = '';
      item.RateRows = [];
    } catch (error) {
      ErrorMessage.SetErrorMessage('ANOOP48');
    }
  }

  static pagesInGeneralEditWizzard = [
    {
      fields: [
        {
          fieldName: 'SelectedOfficeIds',
          label: 'Sales/FW Office',
          className: 'col-6',
          type: InputTypesEnum.OfficeMultiSelect,
          validityChecker: ValidityHelper.forRequiredMultiSelectField,
        },
        {
          fieldName: 'Quarter',
          label: 'Quarter',
          className: 'col-6',
          type: InputTypesEnum.Select,
          selectvalues: (item) => item.QuarterOptions,
          showFilterAt: 1000,
          validityChecker: ValidityHelper.forRequiredSelectField,
          onFieldChange: (item) => {
            AddNewOfficeOverheadWindowDescription.syncRateRows(item);
          },
        },
        {
          fieldName: 'RateRows',
          valueField: 'PricingOverheadRate',
          labelField: 'PricingLabel',
          className: 'col-6',
          type: InputTypesEnum.NumberWithTwoDecimalPlaces,
          // Per-input the wizard passes the element value (index defined); the whole-form validity
          // sweep passes the raw array (index undefined) - validate every row there so the Finish
          // button stays disabled until all rates are > 0, like the old panel.
          validityChecker: (e, item, index) =>
            index === undefined && Array.isArray(e)
              ? AddNewOfficeOverheadWindowDescription.validityCheckerForAllRows(e, 'PricingOverheadRate')
              : ValidityHelper.forRequiredNumberFieldGreaterThanZero(e),
          disableClearButton: true,
        },
        {
          fieldName: 'RateRows',
          valueField: 'ActualOverheadRate',
          labelField: 'ActualLabel',
          className: 'col-6',
          type: InputTypesEnum.NumberWithTwoDecimalPlaces,
          validityChecker: (e, item, index) =>
            index === undefined && Array.isArray(e)
              ? AddNewOfficeOverheadWindowDescription.validityCheckerForAllRows(e, 'ActualOverheadRate')
              : ValidityHelper.forRequiredNumberFieldGreaterThanZero(e),
          disableClearButton: true,
        },
      ],
    },
  ];
}
